/**
 * Promoting an athlete: a college or NIL client becomes a pro client, in one
 * confirmed step. Shared by the app (PromoteClientModal) and the MCP
 * (promote_client), so both agree on what "promote" writes.
 *
 * What changes: league, sport, club, status, roster status, ESPN id and
 * headshot. What is remembered: where they came from, in external_ids, so
 * the Book can say "was College - D1 until 5 Sep 2026" and nothing about
 * the athlete's college history has to be reconstructed.
 *
 * No I/O here. The caller fetches the headshot and writes the record.
 */

/** Pro football leagues an athlete can be promoted into. */
export const PRO_PROMOTION_LEAGUES = ['NFL', 'UFL', 'CFL'] as const
export type ProPromotionLeague = (typeof PRO_PROMOTION_LEAGUES)[number]

/** Roster statuses that make sense on the day someone turns pro. */
export const PROMOTION_ROSTER_STATUSES = ['active', 'practice_squad', 'futures', 'ir', 'pup', 'free_agent'] as const
export type PromotionRosterStatus = (typeof PROMOTION_ROSTER_STATUSES)[number]

export interface PromotionInput {
  league: string
  team_name: string
  roster_status: string
  /** ESPN athlete id. ESPN ids are the same across college and NFL for one person. */
  espn_id?: string | null
  /** Already fetched for the new league; null leaves the current headshot alone. */
  headshot_url?: string | null
  /** Who confirmed the promotion; stamped into external_ids. */
  confirmed_by?: string | null
}

export interface PromotionPayload {
  league: string
  sport: string
  team: string
  team_name: string
  status: 'active'
  roster_status: string
  external_ids: Record<string, unknown>
  headshot_url?: string
}

/** Throws with the reason when the input cannot promote anyone. */
export function buildPromotion(
  client: { league?: string | null; external_ids?: unknown },
  input: PromotionInput,
  now: Date = new Date(),
): PromotionPayload {
  const league = String(input.league ?? '').trim().toUpperCase()
  if (!(PRO_PROMOTION_LEAGUES as readonly string[]).includes(league)) {
    throw new Error(`League must be one of ${PRO_PROMOTION_LEAGUES.join(', ')}.`)
  }
  const team = String(input.team_name ?? '').trim()
  if (!team) throw new Error('The club is required. Which team signed them?')
  const rosterStatus = String(input.roster_status ?? '').trim()
  if (!(PROMOTION_ROSTER_STATUSES as readonly string[]).includes(rosterStatus)) {
    throw new Error(`Roster status must be one of ${PROMOTION_ROSTER_STATUSES.join(', ')}.`)
  }

  const previous = parseExternalIds(client.external_ids)
  const espnId = input.espn_id != null && String(input.espn_id).trim() !== '' ? String(input.espn_id).trim() : null
  if (espnId !== null && !/^\d+$/.test(espnId)) throw new Error('ESPN id must be a number.')

  const external_ids: Record<string, unknown> = {
    ...previous,
    ...(espnId ? { espn_id: espnId } : {}),
    promoted_from_league: client.league ?? null,
    promoted_at: now.toISOString(),
    ...(input.confirmed_by ? { promoted_by: input.confirmed_by } : {}),
  }

  const payload: PromotionPayload = {
    league,
    sport: 'Football',
    team,
    team_name: team,
    status: 'active',
    roster_status: rosterStatus,
    external_ids,
  }
  if (input.headshot_url) payload.headshot_url = input.headshot_url
  return payload
}

function parseExternalIds(value: unknown): Record<string, unknown> {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  return typeof value === 'object' ? { ...(value as Record<string, unknown>) } : {}
}
